import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import UserNavbar from "components/UserNavbar/UserNavbar";
import ContactNumber from "components/ContactNumber";
import { useState } from "react";
import {
  FaShieldAlt,
  FaLock,
  FaCreditCard,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaCalendarAlt,
  FaPassport,
  FaChevronDown,
  FaCheckCircle,
  FaFileContract,
  FaHistory,
  FaHeadset,
  FaSms
} from "react-icons/fa";
import {
  MdSecurity,
  MdLink,
  MdUpdate,
  MdContactMail,
  MdArrowForward,
  MdInfo
} from "react-icons/md";

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("1");

  const policyData = [
    {
      id: "1",
      icon: <FaUser className="w-6 h-6" />,
      heading: "Information We Collect",
      subheading: "When you book online or through our call center",
      list: [
        { icon: <FaUser className="w-4 h-4" />, text: "Full name as per travel document" },
        { icon: <FaPhone className="w-4 h-4" />, text: "Telephone number" },
        { icon: <FaEnvelope className="w-4 h-4" />, text: "Email address" },
        { icon: <FaCalendarAlt className="w-4 h-4" />, text: "Date of birth" },
        { icon: <FaPassport className="w-4 h-4" />, text: "Passport details (when required by airlines)" },
      ],
    },
    {
      id: "2",
      icon: <FaCreditCard className="w-6 h-6" />,
      heading: "Payment Information",
      subheading: "Secure payment processing details",
      list: [
        "Complete credit card number",
        "Expiry date",
        "Billing address matching bank records",
        "Written authorization for credit card use",
      ],
    },
    {
      id: "3",
      icon: <FaShieldAlt className="w-6 h-6" />,
      heading: "Purpose of Data Collection",
      subheading: "How we use your information",
      items: [
        {
          title: "Name",
          description: "Required by airlines for seat reservations and auxiliary services"
        },
        {
          title: "Contact Details",
          description: "For transaction confirmation, updates, schedule changes, and emergency communication"
        },
        {
          title: "Date of Birth / Passport",
          description: "Airport security protocols and verification"
        },
        {
          title: "Payment Details",
          description: "Transaction processing and fraud prevention"
        },
      ],
    },
    {
      id: "4",
      icon: <FaLock className="w-6 h-6" />,
      heading: "Data Protection & Sharing",
      paragraph: "Security is our highest priority without compromise.",
      features: [
        "We do not sell or trade personal information",
        "No marketing use of your data",
        "Data shared only with trusted suppliers for transaction processing",
        "All partners are registered business operators",
      ],
    },
    {
      id: "5",
      icon: <MdSecurity className="w-6 h-6" />,
      heading: "Security Measures",
      subheading: "How we protect your information",
      paragraph: "We implement comprehensive security measures to safeguard your data:",
      securityPoints: [
        "End-to-end encryption for all sensitive data transmission",
        "Secure HTTPS protocol for all web communications",
        "Limited employee access on a need-to-know basis",
        "Secure server environments with advanced protection",
        "Regular security audits and compliance checks",
      ],
      note: "You can verify our security by checking for the padlock icon and 'https://' in your browser address bar.",
    },
    {
      id: "6",
      icon: <MdLink className="w-6 h-6" />,
      heading: "Third-Party Links",
      paragraph: "Our website may include third-party links for travel-related information (visa requirements, destination details, hotel photos). These external sites operate under their own privacy policies, which we recommend reviewing.",
    },
    {
      id: "7",
      icon: <FaFileContract className="w-6 h-6" />,
      heading: "Terms & Conditions",
      paragraph: "Social media content related to Maharaja Flights Limited® Limited is not under our direct control. By using our website, you must agree to our Terms & Conditions before completing any purchase transaction.",
    },
    {
      id: "8",
      icon: <FaSms className="w-6 h-6" />,
      heading: "SMS Communication Policy",
      subheading: "US 10DLC Compliance",
      paragraph: "Maharaja Flights Limited may use SMS (text messaging) to communicate with customers regarding flight bookings, travel quotes, ticket confirmations, itinerary updates, and customer service inquiries.",
      smsPolicy: [
        "Customers provide explicit consent to receive SMS messages during phone consultations or when requesting travel services. Message frequency varies based on customer interaction.",
        "Standard message and data rates may apply.",
        "Customers may opt out of receiving SMS messages at any time by replying STOP. For assistance, reply HELP or contact us at 905-244-1300 or 786-733-1300.",
        "We do not share, sell, rent, or distribute mobile phone numbers or SMS opt-in data to third parties for marketing purposes."
      ]
    },
    {
      id: "9",
      icon: <MdUpdate className="w-6 h-6" />,
      heading: "Policy Updates",
      paragraph: "We may update this Privacy Policy periodically. All changes will be posted on this page with the effective date.",
      updateDate: "Last Updated: February 13, 2024",
    },
    {
      id: "10",
      icon: <MdContactMail className="w-6 h-6" />,
      heading: "Contact Us",
      subheading: "Questions or concerns about our Privacy Policy?",
      paragraph: "Please reach out to us at:",
      contact: {
        email: "contact@maharajaflights.com",
        phone: <ContactNumber headingBg={"bg-blue-600"} text="text-white" />,
        hours: "Monday - Friday: 9:00 AM - 6:00 PM EST",
      },
    },
  ];

  return (
    <section className="min-h-screen bg-gray-50">
      <UserNavbar />
      <ScrollToTopOnRouteChange />

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center space-x-4 mb-4">
            <FaShieldAlt className="w-12 h-12" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
              <p className="text-blue-100 mt-2">Maharaja Flights Limited® Limited</p>
            </div>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl">
            Your privacy is important to us. Learn how Maharaja Flights Limited® protects and manages your personal information.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="bg-blue-500 px-4 py-2 rounded-full flex items-center gap-2">
              <FaLock className="w-4 h-4" /> Transparent
            </span>
            <span className="bg-blue-500 px-4 py-2 rounded-full flex items-center gap-2">
              <MdSecurity className="w-4 h-4" /> Secure
            </span>
            <span className="bg-blue-500 px-4 py-2 rounded-full flex items-center gap-2">
              <FaCheckCircle className="w-4 h-4" /> GDPR Compliant
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Desktop */}
          <div className="lg:w-1/4 hidden lg:block">
            <div className="sticky top-24 bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <MdInfo className="w-6 h-6 text-blue-600" />
                <h3 className="font-semibold text-gray-700 text-lg">Quick Navigation</h3>
              </div>
              <nav className="space-y-2">
                {policyData.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      document.getElementById(`section-${item.id}`)?.scrollIntoView({ behavior: 'smooth' });
                      setActiveSection(item.id);
                    }}
                    className={`flex items-center w-full text-left px-4 py-3 rounded-lg transition-all group ${
                      activeSection === item.id
                        ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className="flex-1 font-medium">{item.heading}</span>
                    <MdArrowForward className={`w-4 h-4 transition-transform ${
                      activeSection === item.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`} />
                  </button>
                ))}
              </nav>
              
              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <FaHeadset className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-700">Need Help?</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Contact our privacy team for assistance</p>
                <div className="space-y-2">
                  <ContactNumber headingBg={"bg-transparent"} text="text-blue-600 font-medium" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Table of Contents - Mobile */}
              <div className="lg:hidden border-b">
                <details className="group">
                  <summary className="flex items-center justify-between p-4 cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex items-center gap-3">
                      <MdInfo className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-700">Jump to Section</span>
                    </div>
                    <FaChevronDown className="w-5 h-5 transition-transform group-open:rotate-180 text-gray-500" />
                  </summary>
                  <div className="p-4 border-t bg-white">
                    <nav className="space-y-2">
                      {policyData.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            document.getElementById(`section-${item.id}`)?.scrollIntoView({ behavior: 'smooth' });
                            setActiveSection(item.id);
                          }}
                          className={`flex items-center w-full text-left px-4 py-3 rounded-lg transition-all ${
                            activeSection === item.id
                              ? 'bg-blue-50 text-blue-600'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {item.icon && <span className="mr-3">{item.icon}</span>}
                          <span className="font-medium">{item.heading}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                </details>
              </div>

              {/* Policy Content */}
              <div className="p-6 md:p-8">
                {policyData.map((item) => (
                  <div
                    key={item.id}
                    id={`section-${item.id}`}
                    className="mb-12 last:mb-0 scroll-mt-24"
                  >
                    <div className="flex items-start space-x-4 mb-6">
                      {item.icon && (
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                            {item.icon}
                          </div>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <h2 className="text-2xl font-bold text-gray-900">{item.heading}</h2>
                          <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full inline-flex items-center gap-1 w-fit">
                            <FaFileContract className="w-3 h-3" />
                            Section {item.id}
                          </span>
                        </div>
                        {item.subheading && (
                          <p className="text-gray-600 mt-2">{item.subheading}</p>
                        )}
                      </div>
                    </div>

                    {/* Render different content types */}
                    {item.list && (
                      <ul className="space-y-4 mb-6">
                        {item.list.map((listItem, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            {typeof listItem === 'object' ? (
                              <>
                                <div className="text-blue-500 mt-0.5 flex-shrink-0">{listItem.icon}</div>
                                <span className="text-gray-700">{listItem.text}</span>
                              </>
                            ) : (
                              <>
                                <div className="w-2 h-2 mt-2 rounded-full bg-blue-400 flex-shrink-0"></div>
                                <span className="text-gray-700">{listItem}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {item.items && (
                      <div className="grid gap-4 md:grid-cols-2 mb-6">
                        {item.items.map((contentItem, index) => (
                          <div key={index} className="bg-gray-50 p-5 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              <h4 className="font-semibold text-gray-900">{contentItem.title}</h4>
                            </div>
                            <p className="text-gray-600 text-sm">{contentItem.description}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {item.features && (
                      <div className="mb-6">
                        <p className="text-gray-700 mb-4 text-lg">{item.paragraph}</p>
                        <div className="grid gap-4">
                          {item.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                              <FaCheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.securityPoints && (
                      <div className="mb-6">
                        <p className="text-gray-700 mb-6 text-lg">{item.paragraph}</p>
                        <ul className="space-y-4 mb-6">
                          {item.securityPoints.map((point, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <div className="w-8 h-8 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                                <FaLock className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <span className="text-gray-700">{point}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                        {item.note && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                            <div className="flex items-start gap-3">
                              <MdInfo className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-800">{item.note}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {item.smsPolicy && (
                      <div className="mb-6">
                        <p className="text-gray-700 mb-4 text-lg">{item.paragraph}</p>
                        <ul className="space-y-4">
                          {item.smsPolicy.map((policy, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <div className="w-2 h-2 mt-2 rounded-full bg-blue-400 flex-shrink-0"></div>
                              <span className="text-gray-700">{policy}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {item.paragraph && !item.securityPoints && !item.features && !item.smsPolicy && (
                      <div className="mb-6">
                        <div className="prose max-w-none">
                          <p className="text-gray-700 whitespace-pre-line text-lg leading-relaxed">{item.paragraph}</p>
                        </div>
                        {item.updateDate && (
                          <div className="mt-6 inline-flex items-center space-x-3 bg-gray-100 px-5 py-3 rounded-lg">
                            <FaHistory className="w-5 h-5 text-gray-500" />
                            <div>
                              <span className="text-gray-700 font-medium block">{item.updateDate}</span>
                              <span className="text-sm text-gray-500">Effective date</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {item.contact && (
                      <div className="mt-8 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <div className="grid md:grid-cols-2 gap-8">
                          <div>
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <FaEnvelope className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-lg">Email</h4>
                                <p className="text-sm text-gray-600">For privacy-related inquiries</p>
                              </div>
                            </div>
                            <a
                              href={`mailto:${item.contact.email}`}
                              className="inline-flex items-center space-x-3 text-blue-600 hover:text-blue-800 font-medium text-lg"
                            >
                              <span>{item.contact.email}</span>
                              <MdArrowForward className="w-5 h-5" />
                            </a>
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <FaPhone className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-lg">Phone</h4>
                                <p className="text-sm text-gray-600">Direct support line</p>
                              </div>
                            </div>
                            <div className="text-gray-700">
                              {item.contact.phone}
                            </div>
                            {item.contact.hours && (
                              <p className="text-sm text-gray-600 mt-3 flex items-center gap-2">
                                <FaCalendarAlt className="w-4 h-4" />
                                {item.contact.hours}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {item.id !== policyData[policyData.length - 1].id && (
                      <div className="border-t pt-8 mt-8 border-gray-200"></div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer Note */}
              <div className="bg-gradient-to-r from-gray-900 to-blue-900 text-white p-8 md:p-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-600 flex items-center justify-center">
                    <FaShieldAlt className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">Committed to Your Privacy</h3>
                  <p className="text-gray-300 text-lg mb-6">
                    Maharaja Flights Limited® Limited adheres to international privacy standards and continuously works to enhance our security measures to protect your personal information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}